import React, { memo, useState } from 'react'
import { Box, Image, Portal } from '@chakra-ui/core'
import { RotateCcw } from 'lucide-react'
import FsLightbox from 'fslightbox-react'
import { FileTile } from './FileTile'
import FilePreview from 'components/CommonComponents/FilePreview'
import { getFileMetadata, isPreviewableImageFile } from 'components/UploadedFileList/fileMetadata'
import useLocaleText from 'components/useLocaleText'
import { MediaUploadPreview } from '../MediaUploadPreview'
import { RemoveButton } from '../Button/RemoveButton'
import { Button } from '../Button'
import { Loader } from '../Loader'
import { mediaGalleryText } from './literals'
import styles from './styles.module.css'

interface MediaGalleryProps {
  attachedImages: string[]
  attachedFiles: string[]
  attachedVideos: string[]
  fileAppearance?: 'default' | 'type'
  isUploadLoading?: boolean
  isShowRemoveIcon?: boolean
  onRemoveMedia?: (index: number) => void
  onRemoveFile?: (url: string) => void
  draftImageUrls?: Set<string>
  uploadingUrls?: Set<string>
  failedUrls?: Set<string>
  onRetryMedia?: (index: number) => void
  onRetryFile?: (url: string) => void
  onImageClick?: (imageIndex: number) => void
}

const MediaGallery: React.FC<MediaGalleryProps> = memo(
  ({
    attachedImages,
    attachedFiles,
    attachedVideos,
    fileAppearance = 'default',
    onRemoveMedia,
    onRemoveFile,
    isUploadLoading = false,
    isShowRemoveIcon = true,
    draftImageUrls,
    uploadingUrls,
    failedUrls,
    onRetryMedia,
    onRetryFile,
    onImageClick,
  }) => {
    const t = useLocaleText(mediaGalleryText)

    const [isOpenLightBox, setIsOpenLightBox] = useState(false)
    const [lightboxIndex, setLightboxIndex] = useState(0)

    const media = [...attachedImages, ...attachedVideos]
    const mediaTypes = [...attachedImages.map(() => 'image' as const), ...attachedVideos.map(() => 'video' as const)]
    const expanded = media.length === 1 && !isShowRemoveIcon

    const openLightbox = (index: number) => {
      setLightboxIndex(index)
      setIsOpenLightBox((prev) => !prev)
    }

    const openMedia = (index: number) => {
      if (index < attachedImages.length && onImageClick) onImageClick(index)
      else openLightbox(index)
    }

    const isUploading = (url: string) =>
      uploadingUrls?.has(url) ?? (isUploadLoading && (url.startsWith('blob:') || !!draftImageUrls?.has(url)))

    if (!media.length && !attachedFiles.length) return null

    return (
      <>
        <div className={styles.gallery} style={{ justifyContent: expanded ? 'center' : undefined }}>
          {expanded ? (
            <Box
              position="relative"
              width="70%"
              minW="min(300px, 100%)"
              height="300px"
              borderRadius="10px"
              overflow="hidden"
              cursor="pointer"
            >
              {mediaTypes[0] === 'video' ? (
                <video
                  src={media[0]}
                  controls
                  onClick={() => openLightbox(0)}
                  style={{ objectFit: 'none', width: '100%', height: '100%' }}
                />
              ) : (
                <Image
                  src={media[0]}
                  width="100%"
                  height="100%"
                  alt={t.uploaded.replace('{index}', '0')}
                  objectFit="cover"
                  onClick={() => openMedia(0)}
                />
              )}
            </Box>
          ) : (
            media.map((url, index) => {
              const name = t.item.replace('{index}', String(index + 1))
              const failed = !!failedUrls?.has(url)
              const uploading = isUploading(url)

              return (
                <MediaUploadPreview
                  key={url}
                  src={url}
                  mediaType={mediaTypes[index]}
                  alt={name}
                  style={{ width: '100px', height: '100px' }}
                  className={styles.thumbnail}
                  uploading={uploading}
                  error={failed}
                  errorLabel={t.failed}
                  previewDisabled={uploading}
                  previewLabel={t.preview.replace('{name}', name)}
                  uploadingLabel={t.uploading.replace('{name}', name)}
                  onPreview={() => openMedia(index)}
                  removeAction={
                    isShowRemoveIcon && onRemoveMedia
                      ? {
                          label: t.remove.replace('{name}', name),
                          onClick: () => onRemoveMedia(index),
                        }
                      : undefined
                  }
                  overlay={
                    <>
                      {failed && onRetryMedia && (
                        <Button
                          type="button"
                          variant="icon"
                          className={styles.retry}
                          aria-label={t.retry.replace('{name}', name)}
                          onClick={() => onRetryMedia(index)}
                        >
                          <RotateCcw size={14} />
                        </Button>
                      )}
                    </>
                  }
                />
              )
            })
          )}
          {attachedFiles.map((url) => {
            const uploading = isUploading(url)
            const failed = !!failedUrls?.has(url)
            const metadata = getFileMetadata(url)
            const name = metadata.name || t.file
            const retry =
              failed && onRetryFile ? (
                <Button
                  type="button"
                  variant="icon"
                  className={styles.retry}
                  aria-label={t.retry.replace('{name}', name)}
                  onClick={() => onRetryFile(url)}
                >
                  <RotateCcw size={14} />
                </Button>
              ) : null

            if (fileAppearance === 'type' || isPreviewableImageFile(metadata)) {
              return (
                <FileTile
                  key={url}
                  url={url}
                  uploading={uploading}
                  failed={failed}
                  onRemove={isShowRemoveIcon ? onRemoveFile : undefined}
                  onRetry={onRetryFile}
                />
              )
            }

            return (
              <div key={url} className={styles.file} aria-busy={uploading && !failed}>
                <FilePreview
                  fileUrl={url}
                  fileName={name}
                  title={name}
                  {...(uploading || failed ? { as: 'div', href: undefined } : {})}
                />
                {uploading && !failed && (
                  <span className={styles.fileUploading} role="status" aria-label={t.uploading.replace('{name}', name)}>
                    <Loader size="md" color="#FFFFFF" h="100%" />
                  </span>
                )}
                {failed && <span className={styles.fileError}>{t.failed}</span>}
                {isShowRemoveIcon && onRemoveFile && (
                  <RemoveButton
                    className={styles.remove}
                    aria-label={t.remove.replace('{name}', name)}
                    onClick={() => onRemoveFile(url)}
                  />
                )}
                {retry}
              </div>
            )
          })}
        </div>
        <Portal container={document.getElementById('root')}>
          <FsLightbox
            key={JSON.stringify(media)}
            toggler={isOpenLightBox}
            sources={media}
            sourceIndex={lightboxIndex}
            types={mediaTypes}
          />
        </Portal>
      </>
    )
  }
)

export default MediaGallery
